import React, { useState, useEffect, useRef } from "react";
import api from "@/api";

export default function UserModal({
  open,
  onClose,
  onCreated,
  onUpdated, // NEW: Callback for when user is updated
  existingInitials = [],
  editingUser = null, // NEW: User data when editing
}) {
  const [name, setName] = useState("");
  const [initials, setInitials] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [rolesSelected, setRolesSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const modalRef = useRef(null);

  const isEditing = !!editingUser;

  // Initialize form with editing user data
  useEffect(() => {
    if (editingUser) {
      setName(editingUser.name || "");
      setInitials(editingUser.initials || "");
      setEmail(editingUser.email || "");
      setRolesSelected(editingUser.roles || []);
    } else {
      // Reset form for new user
      setName("");
      setInitials("");
      setEmail("");
      setRolesSelected([]);
    }
    setPassword("");
    setPasswordConfirm("");
    setError(null);
  }, [editingUser, open]);

  // Suggest initials from name if creating new user and initials is empty
  useEffect(() => {
    if (!name || isEditing) return; // Don't auto-suggest when editing

    const guessInitials = (name) => {
      const words = name.trim().split(/\s+/);
      let guess = "";
      if (words.length === 1) {
        guess = words[0].substring(0, 3).toUpperCase();
      } else {
        guess = words
          .map((w) => w[0])
          .join("")
          .toUpperCase();
      }
      return guess.substring(0, 3);
    };

    setInitials((prev) => {
      const guess = guessInitials(name);
      if (!prev || prev === guessInitials(prev)) {
        return guess;
      }
      return prev;
    });
  }, [name, isEditing]);

  useEffect(() => {
    if (!open) return;

    // Fetch roles when modal opens
    api
      .post("/masterdata/roles", {})
      .then((res) => setRoles(res.data || []))
      .catch(() => setRoles([]));

    // Dismiss on Esc key
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Refused rather than changed: initials are shown as typed (the API refuses them too)
    if (/\s/.test(initials)) {
      setError("Initials cannot contain spaces.");
      return;
    }
    // The admin types the password twice and gives it to the user (no e-mail is sent). Editing: empty keeps it.
    const setsPassword = !isEditing || password !== "" || passwordConfirm !== "";
    if (setsPassword) {
      if (password.length < 8) {
        setError("The password must be at least 8 characters.");
        return;
      }
      if (password !== passwordConfirm) {
        setError("The passwords do not match.");
        return;
      }
    }
    const passwordFields = setsPassword ? { password, password_confirmation: passwordConfirm } : {};
    const initialsUpper = initials.toUpperCase();

    // Check for duplicate initials only if creating new user or changing initials
    if (!isEditing || editingUser.initials !== initialsUpper) {
      if (existingInitials.includes(initialsUpper)) {
        setError("Initials already in use by another user");
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditing) {
        // Update existing user
        await api.post(`/masterdata/users/store`, {
          id: editingUser.id,
          name,
          initials: initialsUpper,
          email,
          roles: rolesSelected,
          ...passwordFields,
        });
        onUpdated && onUpdated();
      } else {
        // Create new user
        const res = await api.post("/masterdata/users/store", {
          name,
          initials: initialsUpper,
          email,
          roles: rolesSelected,
          ...passwordFields,
        });
        // Any warning from the API about the new user
        (res.data?.warnings || []).forEach((message) => {
          window.dispatchEvent(new CustomEvent("app:toast", { detail: { type: "error", message } }));
        });
        onCreated && onCreated();
      }

      // Reset form
      setName("");
      setInitials("");
      setEmail("");
      setPassword("");
      setPasswordConfirm("");
      setRolesSelected([]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          `Failed to ${isEditing ? "update" : "create"} user`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md relative"
      >
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="text-lg font-bold mb-4">
          {isEditing ? "Edit User" : "Add User"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Initials</label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              value={initials}
              onChange={(e) => setInitials(e.target.value)}
              required
              maxLength={8}
              placeholder="ABC"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              className="w-full border rounded px-2 py-1 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="user-password" className="block text-sm font-medium mb-1">Password</label>
            <input
              id="user-password"
              type="password"
              autoComplete="new-password"
              className="w-full border rounded px-2 py-1 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="user-password-confirm" className="block text-sm font-medium mb-1">Confirm password</label>
            <input
              id="user-password-confirm"
              type="password"
              autoComplete="new-password"
              className="w-full border rounded px-2 py-1 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
            <div className="text-xs text-gray-500 mt-1">
              {isEditing
                ? "Leave both empty to keep the password. Otherwise at least 8 characters."
                : "At least 8 characters. Give it to the user: no e-mail is sent."}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Roles</label>
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => {
                const value = typeof r === "object" && r !== null ? r.id || r.name : r;
                const label = typeof r === "object" && r !== null ? r.name || r.id || r : r;
                return (
                    <label key={value} className="inline-flex items-center gap-1">
                      <input
                          type="checkbox"
                          value={value}
                          checked={rolesSelected.includes(label)} // Check if rolesSelected includes the role name
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRolesSelected((prev) => [...prev, label]);
                            } else {
                              setRolesSelected((prev) => prev.filter((v) => v !== label));
                            }
                          }}
                      />
                      <span>{label}</span>
                    </label>
                );
              })}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Check one or more roles, or leave empty for no roles.
            </div>
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-1.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={saving}
            >
              {saving ? "Saving..." : isEditing ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
